import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Step tracking
  // New flow: 'questionPaper' | 'reviewKey' | 'answerSheets' | 'results'
  // Legacy flow: 'upload' | 'review' | 'answerKey' | 'evaluate' | 'results'
  currentStep: 'questionPaper',

  // Question paper upload (new flow)
  qpUploadLoading: false,
  qpResult: null, // { assessmentId, questions, extractedAnswerKey, generalInstructions, extractionMetadata }
  qpUploadError: null,

  // Upload & extraction (legacy combined-mode flow)
  uploadLoading: false,
  extractionResult: null, // { assessmentId, questions, preExtractedAnswers, answerSheetUrl, ... }
  uploadError: null,

  // Answer key
  answerKeyLoading: false,
  answerKey: null,
  answerKeyError: null,
  // Per-row regen state keyed by questionNumber. Lets multiple rows show
  // independent spinners and errors without blocking the rest of the table.
  // Shape: { [questionNumber]: { loading: bool, error?: string, exhausted?: bool } }
  regenStateByQuestion: {},

  // Evaluation
  evaluateLoading: false,
  evaluationResult: null,
  evaluateError: null,

  // List
  listLoading: false,
  scanEvaluations: [],
  pagination: { page: 1, limit: 20, total: 0, totalPages: 0 },
  listError: null,
};

const scanEvaluationSlice = createSlice({
  name: 'scanEvaluation',
  initialState,
  reducers: {
    setCurrentStep: (state, action) => {
      state.currentStep = action.payload;
    },

    // Question paper upload (new flow)
    setQpUploadLoading: (state, action) => {
      state.qpUploadLoading = action.payload;
      if (action.payload) state.qpUploadError = null;
    },
    setQpResult: (state, action) => {
      state.qpResult = action.payload;
      state.qpUploadLoading = false;
      state.qpUploadError = null;
      state.currentStep = 'reviewQuestions';
    },
    setQpUploadError: (state, action) => {
      state.qpUploadLoading = false;
      state.qpUploadError = action.payload;
    },
    advanceToAnswerSheets: (state) => {
      state.currentStep = 'answerSheets';
    },
    // Replace the editable list of paper-level instructions on the
    // QP-acceptance step. Source pre-fills from vision-LLM extraction
    // (setQpResult); teacher edits route through here.
    updateQpInstructions: (state, action) => {
      if (state.qpResult) {
        state.qpResult.generalInstructions = Array.isArray(action.payload)
          ? action.payload
          : [];
      }
    },

    // Upload & extract
    setUploadLoading: (state, action) => {
      state.uploadLoading = action.payload;
      if (action.payload) state.uploadError = null;
    },
    setExtractionResult: (state, action) => {
      state.extractionResult = action.payload;
      state.uploadLoading = false;
      state.uploadError = null;
      state.currentStep = 'review';
    },
    setUploadError: (state, action) => {
      state.uploadLoading = false;
      state.uploadError = action.payload;
    },

    // Answer key
    setAnswerKeyLoading: (state, action) => {
      state.answerKeyLoading = action.payload;
      if (action.payload) state.answerKeyError = null;
    },
    setAnswerKey: (state, action) => {
      state.answerKey = action.payload;
      state.answerKeyLoading = false;
      state.answerKeyError = null;
      // Legacy flow: auto-advance to 'evaluate'.
      // New flow (reviewKey): handled by component — AI stays for review,
      // manual/file auto-advance via setAnswerKeyAndAdvance.
      if (state.currentStep !== 'reviewKey') {
        state.currentStep = 'evaluate';
      }
    },
    setAnswerKeyAndAdvance: (state, action) => {
      state.answerKey = action.payload;
      state.answerKeyLoading = false;
      state.answerKeyError = null;
      state.currentStep = 'answerSheets';
    },
    setAnswerKeyError: (state, action) => {
      state.answerKeyLoading = false;
      state.answerKeyError = action.payload;
    },

    // Per-row regenerate: start a regen attempt for one question.
    regenAnswerStart: (state, action) => {
      const qNum = action.payload;
      state.regenStateByQuestion[qNum] = { loading: true, error: null, exhausted: false };
    },
    // Per-row regenerate: replace the answer-key entry for the question
    // with the regenerated result. Updates answerKey.answerKey in place so
    // the review table re-renders with the new confidence + answer.
    regenAnswerSuccess: (state, action) => {
      const { questionNumber, correctAnswer, confidence, exhausted } = action.payload;
      state.regenStateByQuestion[questionNumber] = { loading: false, error: null, exhausted: Boolean(exhausted) };
      if (state.answerKey?.answerKey) {
        const idx = state.answerKey.answerKey.findIndex(a => a.questionNumber === questionNumber);
        if (idx >= 0) {
          state.answerKey.answerKey[idx] = {
            ...state.answerKey.answerKey[idx],
            correctAnswer,
            confidence,
          };
        }
      }
    },
    regenAnswerError: (state, action) => {
      const { questionNumber, error, exhausted } = action.payload;
      state.regenStateByQuestion[questionNumber] = {
        loading: false,
        error: error || 'Regeneration failed',
        exhausted: Boolean(exhausted),
      };
    },

    // Evaluate
    setEvaluateLoading: (state, action) => {
      state.evaluateLoading = action.payload;
      if (action.payload) state.evaluateError = null;
    },
    setEvaluationResult: (state, action) => {
      state.evaluationResult = action.payload;
      state.evaluateLoading = false;
      state.evaluateError = null;
      state.currentStep = 'results';
    },
    setEvaluateError: (state, action) => {
      state.evaluateLoading = false;
      state.evaluateError = action.payload;
    },

    // List
    setListLoading: (state, action) => {
      state.listLoading = action.payload;
      if (action.payload) state.listError = null;
    },
    setListData: (state, action) => {
      state.scanEvaluations = action.payload.evaluations;
      state.pagination = action.payload.pagination;
      state.listLoading = false;
    },
    setListError: (state, action) => {
      state.listLoading = false;
      state.listError = action.payload;
    },

    // Reset
    resetScanEvaluation: () => initialState,
  },
});

export const {
  setCurrentStep,
  setQpUploadLoading,
  setQpResult,
  setQpUploadError,
  advanceToAnswerSheets,
  updateQpInstructions,
  setUploadLoading,
  setExtractionResult,
  setUploadError,
  setAnswerKeyLoading,
  setAnswerKey,
  setAnswerKeyAndAdvance,
  setAnswerKeyError,
  regenAnswerStart,
  regenAnswerSuccess,
  regenAnswerError,
  setEvaluateLoading,
  setEvaluationResult,
  setEvaluateError,
  setListLoading,
  setListData,
  setListError,
  resetScanEvaluation,
} = scanEvaluationSlice.actions;

export default scanEvaluationSlice.reducer;
